import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SEOMeta title="文章未找到" description="抱歉，您查找的文章不存在或已被移除。" />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '里番动漫精选',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://your-site.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://your-site.com/category/${category.slug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://your-site.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name, href: `/category/${category.slug}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
              <span className="text-xs text-foreground-500">{article.publishDate}</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 text-sm text-foreground-600">
              <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-primary-600"></i>
              </span>
              <span className="font-medium">{article.author}</span>
              <span className="text-foreground-400">|</span>
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-accent-500 text-xs"></i></span>
                <span>{article.rating}</span>
              </div>
            </div>
          </div>

          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 里番动漫精选推荐`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
              prose-strong:text-foreground-900
              prose-li:text-foreground-700 prose-li:text-base
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:mt-10 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:mt-8 [&_h3]:mb-3
              [&_ul]:my-4 [&_ul]:pl-5 [&_ul]:list-disc
              [&_ol]:my-4 [&_ol]:pl-5 [&_ol]:list-decimal
              [&_li]:my-1
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm text-foreground-600 font-medium whitespace-nowrap">分享文章：</span>
              <button className="w-9 h-9 rounded-lg bg-background-100 hover:bg-primary-100 flex items-center justify-center transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-foreground-700"></i>
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 hover:bg-primary-100 flex items-center justify-center transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-foreground-700"></i>
              </button>
              <button className="w-9 h-9 rounded-lg bg-background-100 hover:bg-primary-100 flex items-center justify-center transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-foreground-700"></i>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}